import React from 'react';
import Icon from '../../../components/AppIcon';

const DayDetailsBottomSheet = ({ isOpen, date, payments, onClose }) => {
  if (!isOpen) return null;

  const formatCurrency = (amount, type) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    const value = parseFloat(amount);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(Math.abs(value));
    
    // Show income as positive, expenses as they are
    return type === 'income' ? `+${formatted}` : formatted;
  };

  const formatDate = (date) => {
    if (!date || isNaN(date?.getTime())) return '';
    try {
      return date?.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return '';
    }
  };

  const getTotalAmount = () => {
    if (!payments || payments?.length === 0) return 0;
    return payments?.reduce((sum, payment) => {
      const amount = parseFloat(payment?.amount) || 0;
      // Income adds to total, expenses subtract
      return payment?.type === 'income' ? sum + amount : sum - amount;
    }, 0);
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-[100] flex items-end"
      onClick={handleBackdropClick}
    >
      <div className="bg-card w-full rounded-t-2xl border-t border-border max-h-[70vh] overflow-hidden flex flex-col animate-slide-up">
        {/* Header */}
        <div className="p-16 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-2">
                {formatDate(date)}
              </h3>
              <p className="text-sm text-muted-foreground">
                {payments?.length === 0
                  ? 'No payments scheduled'
                  : `${payments?.length} payment${payments?.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-8 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} color="var(--color-foreground)" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-16">
          {payments?.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-48 h-48 mx-auto mb-12 bg-muted/30 rounded-full flex items-center justify-center">
                <Icon name="Calendar" size={24} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm text-muted-foreground">
                No payments scheduled
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {payments?.map((payment, index) => (
                <div
                  key={`${payment?.id}-${index}`}
                  className="p-12 bg-background rounded-lg border border-border"
                >
                  <div className="flex items-start gap-12">
                    <div
                      className="w-36 h-36 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: `${payment?.categoryColor || '#6366f1'}20`
                      }}
                    >
                      <Icon
                        name={payment?.categoryIcon || 'Tag'}
                        size={18}
                        color={payment?.categoryColor || '#6366f1'}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-8 mb-4">
                        <h4 className="text-sm font-semibold text-foreground truncate">
                          {payment?.name || 'Unnamed Payment'}
                        </h4>
                        <span className={`text-sm font-bold flex-shrink-0 ${
                          payment?.type === 'income' ? 'text-green-600' : 'text-foreground'
                        }`}>
                          {formatCurrency(payment?.amount, payment?.type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-8 text-xs text-muted-foreground">
                        {payment?.type && (
                          <span className={`px-6 py-2 rounded text-xs font-medium ${
                            payment?.type === 'income' ?'bg-green-100 text-green-700' :'bg-blue-100 text-blue-700'
                          }`}>
                            {payment?.type === 'income' ? 'Income' : 'Expense'}
                          </span>
                        )}
                        {payment?.category && (
                          <>
                            <span>•</span>
                            <span>{payment?.category}</span>
                          </>
                        )}
                        {payment?.isRecurring && payment?.frequency && (
                          <>
                            <span>•</span>
                            <span className="capitalize">{payment?.frequency}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {payments?.length > 0 && (
          <div className="p-16 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Total:
              </span>
              <span className="text-base font-bold text-foreground">
                {formatCurrency(getTotalAmount())}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayDetailsBottomSheet;